import { NavLink } from 'react-router-dom';

import heroVideo from '../assets/home_video.mp4';
import seasoningsBg from '../assets/seasonings-bg.jpg';
import { useTranslations } from '../i18n';

export function HomePage() {
  const { t } = useTranslations();
  const hero = t('home.hero', {
    heading: 'Checkpoint Charly — history, great food, and jazz in the heart of Sofia',
    body: 'Discover a cherished Sofia dining room where contemporary Bulgarian plates, curated wines, and welcoming hospitality take center stage—complete with intimate live jazz every Friday night.',
    menuCta: 'Explore the menu',
    bookingCta: 'Book a table',
  }) as {
    heading: string;
    body: string;
    menuCta: string;
    bookingCta: string;
  };
  const booking = t('home.booking', {
    heading: 'Reserve by phone',
    body: 'Call our team to save a table. We will confirm straight away.',
    callCta: 'Call +359 886 577 342',
  }) as {
    heading: string;
    body: string;
    callCta: string;
  };
  const concept = t('home.concept', {
    heading: 'More than a restaurant',
    body:
      'Inspired by the famed Berlin Wall crossing, Checkpoint Charly serves vibrant, authentic cuisine alongside a history from an era that feels unimaginable today.',
    linkLabel: 'Read the full story',
  }) as {
    heading: string;
    body: string;
    linkLabel: string;
  };
  const reviews = t('home.reviews', {
    heading: 'Guests love the atmosphere',
    ratingLabel: 'Average Google rating',
    ratingValue: '4.8 / 5.0',
    footer: 'Real feedback gathered from Google Maps',
    list: [
      {
        name: 'Mina Koleva',
        text: 'Cozy vibe, jazz on point and the staff made sure our anniversary dinner felt special. The duck confit is a must.',
      },
      {
        name: 'Daniel Petrov',
        text: 'Weekly lunch spot for our team. Seasonal dishes, quick service, and they always suggest the perfect wine.',
      },
      {
        name: 'Alexandra Vassileva',
        text: 'Friday nights are magical – candlelight, live quartet, and desserts that disappear too fast. Highly recommend booking ahead.',
      },
    ],
  }) as {
    heading: string;
    ratingLabel: string;
    ratingValue: string;
    footer: string;
    list: { name: string; text: string }[];
  };

  const fullStars = Array.from({ length: 5 });

  return (
    <main>
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-20">
          <video
            className="h-full w-full object-cover"
            src={heroVideo}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
          <div
            className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(9,12,15,0.25)_0%,rgba(9,12,15,0.82)_65%)]"
            aria-hidden="true"
          />
        </div>

        <div className="relative mx-auto flex min-h-[calc(100vh-4rem)] max-w-6xl flex-col items-center justify-center gap-8 px-6 py-24 text-center">
          <div className="space-y-6">
            <h1 className="home-hero__heading text-4xl font-heading font-semibold tracking-[0.12em] text-[var(--color-text-primary)] sm:text-5xl">
              {hero.heading}
            </h1>
            <p className="home-hero__body mx-auto max-w-2xl text-base text-[var(--color-text-primary)]/80 sm:text-lg">
              {hero.body}
            </p>
          </div>

          <div className="home-hero__cta flex flex-wrap justify-center gap-4">
            <NavLink
              to="/menu"
              className="glass-button glass-button--accent inline-flex items-center justify-center px-6 py-3 text-sm uppercase tracking-[0.24em]"
            >
              {hero.menuCta}
            </NavLink>
            <a
              href="#book-table"
              className="glass-button glass-button--ghost inline-flex items-center justify-center px-6 py-3 text-sm uppercase tracking-[0.24em]"
            >
              {hero.bookingCta}
            </a>
          </div>
        </div>
      </section>

      <div
        className="bg-cover bg-fixed bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(12,16,20,0.9) 0%, rgba(12,16,20,0.88) 100%), url(${seasoningsBg})`,
        }}
      >
        <section
          id="book-table"
          className="scroll-mt-36"
        >
          <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 py-24 text-center">
            <div className="flex flex-col gap-6 rounded-[32px] border border-white/12 bg-[rgba(10,14,18,0.78)]/95 p-10 shadow-[0_40px_120px_-70px_rgba(0,0,0,0.85)] backdrop-blur">
              <h2 className="text-3xl font-heading font-semibold text-[var(--color-text-primary)]">
                {booking.heading}
              </h2>
              <p className="text-base text-[var(--color-text-primary)]/80 sm:text-lg">
                {booking.body}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="tel:+359886577342"
                  className="glass-button glass-button--accent inline-flex items-center justify-center px-8 py-3 text-sm uppercase tracking-[0.24em]"
                >
                  {booking.callCta}
                </a>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="mx-auto w-full max-w-6xl px-6 py-20">
            <div className="mb-10 flex flex-col gap-4 text-center sm:flex-row sm:items-end sm:justify-between sm:text-left">
              <div>
                <h2 className="text-3xl font-heading font-semibold text-[var(--color-text-primary)]">
                  {reviews.heading}
                </h2>
                <p className="text-sm uppercase tracking-[0.28em] text-[var(--color-text-muted)]">
                  {reviews.ratingLabel}
                </p>
              </div>
              <div className="flex items-center justify-center gap-3 rounded-full border border-white/15 bg-white/5 px-5 py-2 text-[var(--color-accent-bright)] shadow-[0_24px_68px_-40px_rgba(194,103,58,0.6)]">
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-6 w-6"
                >
                  <path d="M11.48 2.5a.75.75 0 0 1 1.04 0l2.53 2.52 3.41.5a.75.75 0 0 1 .42 1.28l-2.47 2.41.58 3.4a.75.75 0 0 1-1.09.79L12 12.64l-3.04 1.6a.75.75 0 0 1-1.09-.79l.58-3.4-2.47-2.41a.75.75 0 0 1 .42-1.28l3.41-.5 2.53-2.52Z" />
                </svg>
                <span className="text-lg font-semibold tracking-[0.24em]">
                  {reviews.ratingValue}
                </span>
              </div>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {reviews.list.map((review) => (
                <article
                  key={review.name}
                  className="flex h-full flex-col gap-4 rounded-[28px] border border-white/10 bg-[rgba(16,22,27,0.9)] p-6 text-left shadow-[0_38px_96px_-60px_rgba(0,0,0,0.7)]"
                >
                  <div className="flex items-center justify-between gap-2 text-sm text-[var(--color-text-muted)]">
                    <span className="tracking-[0.2em] text-[var(--color-text-primary)]/90">{review.name}</span>
                    <div className="flex items-center gap-1 text-[var(--color-accent-bright)]">
                      {fullStars.map((_, index) => (
                        <svg
                          aria-hidden="true"
                          key={`${review.name}-star-${index}`}
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="h-4 w-4"
                        >
                          <path d="M9.657 2.23a.75.75 0 0 1 1.686 0l1.02 3.141a.75.75 0 0 0 .713.518h3.304c.68 0 .96.87.412 1.27l-2.673 1.942a.75.75 0 0 0-.272.837l1.02 3.141c.21.648-.53 1.188-1.08.787l-2.672-1.942a.75.75 0 0 0-.882 0l-2.672 1.942c-.55.401-1.29-.139-1.08-.787l1.02-3.141a.75.75 0 0 0-.272-.837L3.208 7.16c-.548-.4-.268-1.27.412-1.27h3.305a.75.75 0 0 0 .712-.518Z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed text-[var(--color-text-primary)]/80">
                    “{review.text}”
                  </p>
                </article>
              ))}
            </div>
            <p className="mt-10 text-center text-xs uppercase tracking-[0.3em] text-[var(--color-text-muted)]">
              {reviews.footer}
            </p>
          </div>
        </section>

        <section>
          <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 py-20 text-center">
            <div className="flex flex-col gap-6 rounded-[32px] border border-white/12 bg-[rgba(9,12,15,0.78)]/90 p-10 text-center shadow-[0_48px_120px_-60px_rgba(0,0,0,0.85)] backdrop-blur">
              <h2 className="text-3xl font-heading font-semibold text-[var(--color-text-primary)]">
                {concept.heading}
              </h2>
              <p className="text-base text-[var(--color-text-primary)]/80 sm:text-lg">
                {concept.body}
              </p>
              <div className="flex justify-center">
                <NavLink
                  to="/about"
                  className="rounded-full border border-[var(--color-accent)] px-6 py-3 text-sm uppercase tracking-[0.24em] text-[var(--color-accent-bright)] transition hover:bg-[var(--color-accent)] hover:text-white"
                >
                  {concept.linkLabel}
                </NavLink>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
